import http from 'http';
import https from 'https';
import httpProxy from 'http-proxy';
import { URL } from 'url';
import { pino } from 'pino';
import type { ProxyConfig, ProxyRequest } from '../types.js';
import { AuthMiddleware } from './auth.js';
import { RateLimiter } from './rate-limiter.js';

export class ProxyServer {
  private proxy: httpProxy;
  private server: http.Server | null = null;
  private logger: pino.Logger;
  private auth: AuthMiddleware;
  private rateLimiter: RateLimiter;
  private requestCount = 0;

  constructor(private config: ProxyConfig) {
    // 初始化日志
    this.logger = pino({
      level: config.logging?.level || 'info',
      transport: config.logging?.pretty ? {
        target: 'pino-pretty',
        options: {
          colorize: true,
          translateTime: 'HH:MM:ss Z',
          ignore: 'pid,hostname',
        }
      } : undefined,
    });

    // 初始化认证中间件
    this.auth = new AuthMiddleware(config.auth);

    // 初始化速率限制
    this.rateLimiter = new RateLimiter(config.rateLimit);

    // 创建代理实例
    this.proxy = httpProxy.createProxyServer({
      changeOrigin: config.target?.changeOrigin ?? true,
      secure: config.target?.secure ?? false,
      ws: true,
      followRedirects: true,
    });

    this.setupProxyHandlers();
  }

  private setupProxyHandlers(): void {
    // 代理请求事件
    this.proxy.on('proxyReq', (proxyReq, req, res) => {
      const requestId = this.generateRequestId();
      (req as any).requestId = requestId;

      // 添加转发头
      const clientIp = this.getClientIp(req);
      proxyReq.setHeader('X-Forwarded-For', clientIp);
      proxyReq.setHeader('X-Real-IP', clientIp);
      proxyReq.setHeader('X-Request-ID', requestId);

      this.logger.info({
        requestId,
        method: req.method,
        url: req.url,
        headers: req.headers,
      }, 'Proxying request');
    });

    // 代理响应事件
    this.proxy.on('proxyRes', (proxyRes, req, res) => {
      const requestId = (req as any).requestId;
      
      // 添加响应头
      proxyRes.headers['X-Proxy-By'] = 'CodeLook Proxy Server';
      proxyRes.headers['X-Request-ID'] = requestId;

      this.logger.info({
        requestId,
        statusCode: proxyRes.statusCode,
        headers: proxyRes.headers,
      }, 'Proxy response');
    });

    // 错误处理
    this.proxy.on('error', (err, req, res) => {
      const requestId = (req as any).requestId;
      
      this.logger.error({
        requestId,
        error: err.message,
        stack: err.stack,
      }, 'Proxy error');

      if (res instanceof http.ServerResponse && !res.headersSent) {
        res.writeHead(502, { 'Content-Type': 'application/json' });
        res.end(JSON.stringify({
          error: 'Bad Gateway',
          message: 'The proxy server received an invalid response',
          requestId,
        }));
      }
    });
  }

  start(port?: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const serverPort = port || this.config.port || 8080;
      const serverHost = this.config.host || '0.0.0.0';

      this.server = http.createServer(async (req, res) => {
        // 健康检查端点
        if (req.url === '/health') {
          res.writeHead(200, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ 
            status: 'ok', 
            uptime: process.uptime(),
            requests: this.requestCount,
          }));
          return;
        }

        // 认证检查
        if (!this.auth.authenticate(req)) {
          res.writeHead(401, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Unauthorized' }));
          return;
        }

        // 速率限制
        const clientIp = this.getClientIp(req);
        if (!this.rateLimiter.allow(clientIp)) {
          res.writeHead(429, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Too Many Requests' }));
          return;
        }

        // 解析目标URL
        const targetUrl = this.extractTargetUrl(req);
        if (!targetUrl) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'Missing target URL' }));
          return;
        }

        this.requestCount++;

        // 代理请求
        this.proxy.web(req, res, { 
          target: targetUrl,
          headers: {
            host: new URL(targetUrl).host,
          },
        });
      });

      // 处理 CONNECT 请求（HTTPS 代理）
      this.server.on('connect', (req, clientSocket, head) => {
        // 认证检查
        if (!this.auth.authenticate(req)) {
          clientSocket.write('HTTP/1.1 401 Unauthorized\r\n\r\n');
          clientSocket.end();
          return;
        }

        const { hostname, port } = this.parseConnectUrl(req.url!);
        const serverPort = parseInt(port) || 443;

        this.logger.info({
          method: 'CONNECT',
          hostname,
          port: serverPort,
        }, 'Handling CONNECT request');

        const serverSocket = require('net').connect(serverPort, hostname, () => {
          clientSocket.write('HTTP/1.1 200 Connection Established\r\n' +
                           'Proxy-agent: CodeLook Proxy\r\n' +
                           '\r\n');
          serverSocket.write(head);
          serverSocket.pipe(clientSocket);
          clientSocket.pipe(serverSocket);
        });

        serverSocket.on('error', (err: Error) => {
          this.logger.error({
            error: err.message,
            hostname,
            port: serverPort,
          }, 'CONNECT error');
          clientSocket.end();
        });

        clientSocket.on('error', () => {
          serverSocket.end();
        });
      });

      // 启动服务器
      this.server.listen(serverPort, serverHost, () => {
        this.logger.info({
          port: serverPort,
          host: serverHost,
        }, 'Proxy server started');
        resolve();
      });

      this.server.on('error', (err) => {
        this.logger.error({ error: err }, 'Server error');
        reject(err);
      });
    });
  }

  stop(): Promise<void> {
    return new Promise((resolve) => {
      if (this.server) {
        this.server.close(() => {
          this.logger.info('Proxy server stopped');
          resolve();
        });
      } else {
        resolve();
      }
    });
  }

  private extractTargetUrl(req: http.IncomingMessage): string | null {
    // 从请求头获取目标URL
    const targetHeader = req.headers['x-target-url'] as string;
    if (targetHeader) {
      return targetHeader;
    }

    // 从查询参数获取
    const url = new URL(req.url!, `http://${req.headers.host}`);
    const targetParam = url.searchParams.get('target');
    if (targetParam) {
      return targetParam;
    }

    // 从请求URL提取（去掉第一个路径段作为目标）
    const pathMatch = req.url?.match(/^\/proxy\/(https?:\/\/.+)/);
    if (pathMatch) {
      return pathMatch[1];
    }

    return null;
  }

  private parseConnectUrl(url: string): { hostname: string; port: string } {
    const [hostname, port = '443'] = url.split(':');
    return { hostname, port };
  }

  private getClientIp(req: http.IncomingMessage): string {
    const forwarded = req.headers['x-forwarded-for'] as string;
    if (forwarded) {
      return forwarded.split(',')[0].trim();
    }
    return req.socket.remoteAddress || 'unknown';
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}